import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ButtonGroup from "@mui/material/ButtonGroup";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { Divider, Stack, Badge, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ConfirmBox from "../../UiComponents/ConfirmBox/ConfirmBox";
import SuccessImage from "../../Resources/Images/ActiveImage.png";
import FailureImage from "../../Resources/Images/disable.png";
import FloatLabel from "../../UiComponents/FloatLabel/FloatLabel";
import {
    getBriskWallet,
    getBriskWalletAmount,
    getBriskWalletReportByUserId,
    addBriskWalletAmount,
    updateBriskWalletByID,
    getBriskWalletTransStatus,
    getReceiptImage,
} from "../../Service/_index";
import moment from "moment";
import Loader from "../../UiComponents/Loader/Loader";
import { ToastError, ToastSuccess } from "../../UiComponents/Toaster/Toast";
import { checkUserType } from "../../Shared/CommonConstant";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { SearchIcon } from "../../Resources/Icons/icons";
import FilterListIcon from "@mui/icons-material/FilterList";
import MasterCommonTable from "../../UiComponents/CommonTable/MasterCommonTable";
import wallett from "../../Resources/Images/wallet.png";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { DoneOutlineIcon, NotInterestedIcon } from "../../Resources/Icons/icons";
import WalletTopupDialog from "./components/WalletTopupDialog";
import StatusResultDialog from "./components/StatusResultDialog";
import ImageViewerDialog from "./components/ImageViewerDialog";

const StatusOptions = [
    { label: "All", value: "all" },
    { label: "Success", value: "success" },
    { label: "Failure", value: "failure" },
    { label: "Created", value: "created" },
];

const BriskTransactionHistory = () => {
    const UserType = sessionStorage.getItem("userType");
    const location = useLocation();
    const navigate = useNavigate();
    const contextUser = location.state?.userId;
    const contextUserName = location.state?.userName;

    const [transactionData, setTransactionData] = useState([]);
    const [walletBalance, setWalletBalance] = useState(0);
    const [selectedStatus, setSelectedStatus] = useState(StatusOptions[0]); // Default to "All"
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openLoader, setOpenLoader] = useState(false);
    const [isAllow, setIsAllow] = useState({});
    const [showFilter, setShowFilter] = useState(false);
    const [searchText, setSearchText] = useState("");

    // Custom Dialog States
    const [statusUpdateDialog, setStatusUpdateDialog] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState({});
    const [walletTopupDialog, setWalletTopupDialog] = useState(false);
    const [topupAmount, setTopupAmount] = useState("");
    const [topupFile, setTopupFile] = useState(null);
    const [amountError, setAmountError] = useState("");
    const [fileError, setFileError] = useState("");
    const [resultDialog, setResultDialog] = useState({ open: false, type: "", message: "" });
    const [imageViewDialog, setImageViewDialog] = useState({ open: false, imageUrl: "", fileName: "" });

    const handleStatusUpdate = (row, newStatus) => {
        setSelectedTransaction({ ...row, newStatus });
        setStatusUpdateDialog(true);
    };

    const confirmStatusUpdate = () => {
        setOpenLoader(true);
        updateBriskWalletByID(selectedTransaction._id, selectedTransaction.newStatus)
            .then((res) => {
                setOpenLoader(false);
                if (res && !res.error) {
                    setResultDialog({
                        open: true,
                        type: "success",
                        message: "Status updated successfully"
                    });
                    GetTransactionHistory();
                    GetWalletBalance();
                } else {
                    setResultDialog({
                        open: true,
                        type: "error",
                        message: "Failed to update status"
                    });
                }
            })
            .catch((err) => {
                setOpenLoader(false);
                setResultDialog({
                    open: true,
                    type: "error",
                    message: "Failed to update status"
                });
            });
    };

    const handleCheckStatus = async (row) => {
        setOpenLoader(true);
        getBriskWalletTransStatus(row.orderId)
            .then((res) => {
                setOpenLoader(false);
                const status = res?.data?.data;
                if (status === "SUCCESS") {
                    ToastSuccess("Transaction Successful");
                } else {
                    ToastError(status ? status : "Transaction Failed");
                }
            })
            .catch((err) => {
                setOpenLoader(false);
                ToastError("Failed to check status");
            });
    };

    const headCells = [
        {
            id: "sno",
            placeMent: true,
            disablePadding: false,
            label: "S.No",
            type: "index"
        },
        { id: "createdAt", placeMent: true, disablePadding: false, label: "Date" },
        { id: "userName", placeMent: true, disablePadding: false, label: "User" },
        { id: "createdByName", placeMent: true, disablePadding: false, label: "Created By" },
        { id: "updatedByName", placeMent: true, disablePadding: false, label: "Updated By" },
        // { id: "orderId", placeMent: true, disablePadding: false, label: "Order ID" },
        {
            id: "status",
            placeMent: true,
            disablePadding: false,
            label: "Status",
            type: "coloredText",
            getColor: (row) => {
                switch (row.status) {
                    case "SUCCESS": return "green";
                    case "FAILURE": return "red";
                    case "CREATED": return "orange";
                    default: return "inherit";
                }
            }
        },
        {
            id: "amount",
            placeMent: true,
            disablePadding: false,
            label: "Amount",
            type: "component",
            renderComponent: (row) => {
                // Determine color based on status and amount
                let color = "inherit";
                if (row.status === "FAILURE") {
                    color = "orange"; // Yellow/orange for failed transactions
                } else if (row.amount >= 0) {
                    color = "green"; // Green for positive amounts (topups)
                } else {
                    color = "red"; // Red for negative amounts (deductions)
                }

                return (
                    <Typography
                        sx={{
                            color: color,
                            fontWeight: "500",
                        }}
                    >
                        {row.amount >= 0
                            ? `+ ₹ ${Math.abs(row.amount)?.toLocaleString("en-IN") || 0}`
                            : `- ₹ ${Math.abs(row.amount)?.toLocaleString("en-IN") || 0}`}
                    </Typography>
                );
            },
        },
        {
            id: "receipt",
            placeMent: false,
            disablePadding: false,
            label: "Receipt",
            type: "component",
            renderComponent: (row) => {
                if (row.topupReceipt && row.topupReceipt.key) {
                    return (
                        <IconButton
                            size="small"
                            onClick={async () => {
                                setOpenLoader(true);
                                try {
                                    const response = await getReceiptImage(row.topupReceipt.key);
                                    setOpenLoader(false);
                                    if (!response.error && response.url) {
                                        setImageViewDialog({
                                            open: true,
                                            imageUrl: response.url,
                                            fileName: row.topupReceipt.fileName || "Receipt"
                                        });
                                    } else {
                                        ToastError("Failed to load receipt image");
                                    }
                                } catch (error) {
                                    setOpenLoader(false);
                                    ToastError("Failed to load receipt image");
                                }
                            }}
                            sx={{
                                color: "#1E3A5F",
                                "&:hover": { backgroundColor: "#E8F0FE" }
                            }}
                        >
                            <VisibilityIcon />
                        </IconButton>
                    );
                }
                return <Typography variant="caption" color="text.secondary">-</Typography>;
            }
        },
        {
            id: "action",
            placeMent: false,
            disablePadding: false,
            label: "Action",
            type: "component",
            renderComponent: (row) => {
                if (row.status === "CREATED") {
                    return (
                        <ButtonGroup size="small" variant="contained">
                            <Button
                                sx={{
                                    backgroundColor: "#1E3A5F",
                                    "&:hover": { backgroundColor: "#2E5A8F" }
                                }}
                                onClick={() => handleStatusUpdate(row, "SUCCESS")}
                            >
                                Success
                            </Button>
                            <Button
                                sx={{
                                    backgroundColor: "#D4AF37",
                                    color: "#1E3A5F",
                                    "&:hover": { backgroundColor: "#C9A961" }
                                }}
                                onClick={() => handleStatusUpdate(row, "FAILURE")}
                            >
                                Failure
                            </Button>
                            {/* {row.orderId && UserType === "CLIENT" && (
                                <Button
                                    color="info"
                                    onClick={() => handleCheckStatus(row)}
                                >
                                    Check Status
                                </Button>
                            )} */}
                        </ButtonGroup>
                    );
                }
                if (row.status === "SUCCESS") {
                    return <Typography color="success.main">✓ Completed</Typography>;
                }
                if (row.status === "FAILURE") {
                    return <Typography color="error.main">✗ Failed</Typography>;
                }
                return null;
            },
        },
    ];

    useEffect(() => {
        // If contextUser exists but page was refreshed (state lost), redirect back
        if (contextUser && !location.state?.userId) {
            navigate("/briskWallet");
            return;
        }

        const renderData = checkUserType(UserType);
        setIsAllow(renderData);
        GetTransactionHistory();
        GetWalletBalance();
    }, [UserType, contextUser, location.state, navigate]);

    const GetTransactionHistory = async (status = selectedStatus) => {
        setOpenLoader(true);
        const statusValue = status?.value || "all";
        getBriskWallet(statusValue, contextUser) // Pass contextUser to API
            .then((res) => {
                setOpenLoader(false);
                if (res && !res.error && Array.isArray(res)) {
                    const formattedData = res.map((item, index) => ({
                        ...item,
                        __index__: index + 1, // Add index for S.No column
                        createdAt: item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY HH:mm") : "-",
                        amount: Number(item.amount) || 0,
                    }));

                    // No need for client-side filtering - backend handles it
                    setTransactionData(formattedData);
                } else {
                    setTransactionData([]);
                }
                setPage(0);
            })
            .catch((err) => {
                setOpenLoader(false);
                setTransactionData([]);
                ToastError("Failed to fetch transactions");
            });
    };

    const GetWalletBalance = async () => {
        // If we have a contextUser, get their specific balance
        if (contextUser) {
            getBriskWalletReportByUserId(contextUser)
                .then((res) => {
                    if (res && Array.isArray(res) && res.length > 0) {
                        // The array is reversed, so the first item has the latest walletBalance
                        setWalletBalance(res[0]?.walletBalance || 0);
                    } else {
                        setWalletBalance(0);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch wallet balance");
                    setWalletBalance(0);
                });
        } else {
            // Fall back to logged-in user's balance
            getBriskWalletAmount()
                .then((res) => {
                    if (res && !res.error) {
                        setWalletBalance(res.balance || 0);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch wallet balance");
                });
        }
    };

    const handleStatusChange = (e, v) => {
        const newStatus = v || StatusOptions[0];
        console.log("Status changed to:", newStatus);
        setSelectedStatus(newStatus);
        // Auto-apply filter when status changes
        GetTransactionHistory(newStatus);
    };

    const handleWalletTopup = async () => {
        setTopupAmount("");
        setTopupFile(null);
        setAmountError("");
        setFileError("");
        setWalletTopupDialog(true);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setFileError(""); // Clear previous error

        if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setFileError("File size must be less than 5MB");
                event.target.value = null;
                setTopupFile(null);
                return;
            }
            // Validate file type (images and PDFs only)
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                setFileError("Only images (JPEG, PNG, GIF) and PDF files are allowed");
                event.target.value = null;
                setTopupFile(null);
                return;
            }
            setTopupFile(file);
        }
    };

    const confirmWalletTopup = () => {
        // Clear previous errors
        setAmountError("");
        setFileError("");

        let hasError = false;

        // Validate amount
        const numAmount = Number(topupAmount);
        if (!topupAmount || isNaN(numAmount) || numAmount <= 0) {
            setAmountError("Please enter a valid amount");
            hasError = true;
        }

        // Validate file
        // if (!topupFile) {
        //     setFileError("Please upload a receipt/proof of payment");
        //     hasError = true;
        // }

        if (hasError) {
            return;
        }

        setWalletTopupDialog(false);
        setOpenLoader(true);

        // Create FormData to send file
        const formData = new FormData();
        formData.append('amount', numAmount);
        if (contextUser) {
            formData.append('userId', contextUser);
        }
        formData.append('file', topupFile);

        addBriskWalletAmount(formData)
            .then((res) => {
                setOpenLoader(false);
                if (res && !res.error && res.success) {
                    setResultDialog({
                        open: true,
                        type: "success",
                        message: res.message || "Wallet topup successful"
                    });
                    GetTransactionHistory();
                    GetWalletBalance();
                    setTopupAmount("");
                    setTopupFile(null);
                } else if (res.error) {
                    setResultDialog({
                        open: true,
                        type: "error",
                        message: res.message || "Failed to initiate topup"
                    });
                }
            })
            .catch((err) => {
                console.error("Wallet topup error:", err);
                setOpenLoader(false);
                setResultDialog({
                    open: true,
                    type: "error",
                    message: "Failed to initiate topup"
                });
            });
    };

    const formatNumber = (num) => new Intl.NumberFormat("en-IN").format(num);

    const tableConfig = {
        isAction: false,
        isView: false,
        isEdit: false,
        isDelete: false,
        isSearch: false,
        isUpload: false,
        isFileDownload: false,
    };

    const FilteredData = transactionData.filter((el) => {
        const searchTerm = searchText.toLowerCase();
        const transactionDetails = `${el?.userName}, ${el?.orderId}, ${el?.status}, ${el?.amount}`.toLowerCase();
        return transactionDetails.includes(searchTerm);
    });

    const activeFiltersCount = (selectedStatus && selectedStatus.value !== "all") ? 1 : 0;

    return (
        <>
            <div>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ position: "relative", zIndex: 1, mt: 0 }}>
                        <Grid container className="Master_Card_Container">
                            <Grid
                                container
                                alignItems={"center"}
                                className="Master_Card_Sub_Container"
                                sx={{ mb: 2 }}
                            >
                                <Grid item xs={12} sm={6} md={4}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {/* {contextUser && ( */}
                                        <Button
                                            variant="contained"
                                            startIcon={<ArrowBackIcon />}
                                            onClick={() => navigate("/briskWallet")}
                                            sx={{
                                                textTransform: "none",
                                                background: "linear-gradient(135deg, #667085 0%, #475467 100%)",
                                                color: "#fff",
                                                fontFamily: "Inter",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                padding: "8px 20px",
                                                borderRadius: "8px",
                                                boxShadow: "0 2px 8px rgba(102, 112, 133, 0.25)",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    background: "linear-gradient(135deg, #475467 0%, #667085 100%)",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 4px 12px rgba(102, 112, 133, 0.35)",
                                                },
                                                "&:active": {
                                                    transform: "translateY(0)",
                                                },
                                            }}
                                        >
                                            Back
                                        </Button>
                                        {/* )} */}
                                        <Typography className="Master_Card_Tittle">
                                            {contextUserName
                                                ? `Brisk Transaction History - ${contextUserName}`
                                                : "Brisk Transaction History"
                                            }
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={8}
                                    className="d-flex"
                                    justifyContent={{
                                        xs: "start",
                                        sm: "flex-end",
                                        md: "flex-end",
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: "column", sm: "row", md: "row" }}
                                        spacing={2}
                                        mt={{ xs: 2, sm: 2, md: 0 }}
                                    >
                                        <Button
                                            className="Master_Card_Filter_Button w-100"
                                            sx={{ width: "102px", height: "40px", gap: "8px" }}
                                            startIcon={
                                                <Badge
                                                    badgeContent={activeFiltersCount}
                                                    color="error"
                                                    sx={{
                                                        "& .MuiBadge-badge": {
                                                            right: 5,
                                                            top: 3,
                                                            width: "14px",
                                                            height: "14px",
                                                            minWidth: "14px",
                                                            borderRadius: "50%",
                                                            fontSize: "8.91px",
                                                            fontWeight: "600",
                                                            backgroundColor: "#E50000",
                                                            color: "#fff",
                                                            padding: 0,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        },
                                                    }}
                                                >
                                                    <FilterListIcon sx={{ marginRight: "1.5px" }} />
                                                </Badge>
                                            }
                                            onClick={() => setShowFilter(!showFilter)}
                                        >
                                            Filters
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<AccountBalanceWalletIcon />}
                                            onClick={handleWalletTopup}
                                            sx={{
                                                textTransform: "none",
                                                background: "linear-gradient(135deg, #1E3A5F 0%, #2E5A8F 100%)",
                                                color: "#fff",
                                                fontFamily: "Inter",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                padding: "5px 15px",
                                                borderRadius: "8px",
                                                boxShadow: "0 4px 12px rgba(30, 58, 95, 0.3)",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    background: "linear-gradient(135deg, #2E5A8F 0%, #1E3A5F 100%)",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 6px 16px rgba(30, 58, 95, 0.4)",
                                                },
                                                "&:active": {
                                                    transform: "translateY(0)",
                                                },
                                            }}
                                        >
                                            Wallet Topup
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>

                            {showFilter && (
                                <Grid item xs={12} sx={{ mb: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4} md={3}>
                                            <FloatLabel label="Filter by Status" value={selectedStatus?.label || "All"}>
                                                <Autocomplete
                                                    className="AutoComplete_InputBox"
                                                    options={StatusOptions}
                                                    value={selectedStatus}
                                                    onChange={handleStatusChange}
                                                    renderInput={(params) => <TextField {...params} />}
                                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                                />
                                            </FloatLabel>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Divider
                                    sx={{
                                        backgroundColor: "#EAECF0",
                                        borderWidth: 1,
                                    }}
                                />
                            </Grid>

                            <Grid
                                container
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                sx={{ margin: "0px 10px" }}
                            >
                                <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }} sx={{ display: "flex" }}>
                                    <Grid
                                        item
                                        marginLeft={2}
                                        margin={1}
                                        sx={{
                                            backgroundColor: "#F0F5FA",
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "2px",
                                            paddingRight: "15px",
                                            paddingLeft: "15px",
                                            gap: "10px",
                                            border: "1px solid #DFE7F1",
                                        }}
                                    >
                                        <Grid>
                                            <img src={wallett} width="18px" height="17px" alt="wallet" />
                                        </Grid>
                                        <Grid>
                                            <Typography
                                                sx={{
                                                    fontFamily: "Inter",
                                                    fontWeight: "500",
                                                    color: "#0070FF",
                                                    fontSize: 16,
                                                    lineHeight: "1.2",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {formatNumber(transactionData?.length || 0)}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "Inter",
                                                    fontWeight: "500",
                                                    color: "#667085",
                                                    fontSize: 12,
                                                    lineHeight: "1.2",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                Transaction Count
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        item
                                        marginLeft={2}
                                        margin={1}
                                        sx={{
                                            backgroundColor: "#F0F5FA",
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "2px",
                                            paddingRight: "15px",
                                            paddingLeft: "15px",
                                            gap: "10px",
                                            border: "1px solid #DFE7F1",
                                        }}
                                    >
                                        <Grid>
                                            <img src={wallett} width="18px" height="17px" alt="wallet" />
                                        </Grid>
                                        <Grid>
                                            <Typography
                                                sx={{
                                                    fontFamily: "Inter",
                                                    fontWeight: "500",
                                                    color: "#0070FF",
                                                    fontSize: 16,
                                                    lineHeight: "1.2",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                ₹ {formatNumber(walletBalance)}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "Inter",
                                                    fontWeight: "500",
                                                    color: "#667085",
                                                    fontSize: 12,
                                                    lineHeight: "1.2",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                Wallet Balance
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }}>
                                    <Input
                                        focused="false"
                                        className="w-100 Master_Card_Input"
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            setPage(0);
                                        }}
                                        disableUnderline={true}
                                        placeholder="Search..."
                                        startAdornment={
                                            <InputAdornment
                                                position="start"
                                                sx={{
                                                    marginRight: "8px",
                                                }}
                                            >
                                                <SearchIcon />
                                            </InputAdornment>
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <MasterCommonTable
                                    data={FilteredData}
                                    columns={headCells}
                                    tableConfig={tableConfig}
                                    page={page}
                                    setPage={setPage}
                                    rowsPerPage={rowsPerPage}
                                    setRowsPerPage={setRowsPerPage}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </div>


            {/* Status Update Confirmation Dialog */}
            <ConfirmBox
                open={statusUpdateDialog}
                title={selectedTransaction.newStatus === "SUCCESS" ? "Success" : "Disable"}
                content={`Are you sure you want to mark this transaction as ${selectedTransaction.newStatus} ? `}
                confirmButton={selectedTransaction.newStatus === "SUCCESS" ? "Confirm Success" : "Confirm Failure"}
                setOpenConfirmBox={setStatusUpdateDialog}
                Function={confirmStatusUpdate}
                icon={selectedTransaction.newStatus === "SUCCESS" ? <DoneOutlineIcon /> : <NotInterestedIcon />}
                img={selectedTransaction.newStatus === "SUCCESS" ? SuccessImage : FailureImage}
            />

            {/* Wallet Topup Dialog */}
            <WalletTopupDialog
                open={walletTopupDialog}
                onClose={() => setWalletTopupDialog(false)}
                topupAmount={topupAmount}
                setTopupAmount={setTopupAmount}
                topupFile={topupFile}
                handleFileChange={handleFileChange}
                onConfirm={confirmWalletTopup}
                amountError={amountError}
                fileError={fileError}
            />

            {/* Result Dialog (Success/Error) */}
            <StatusResultDialog
                open={resultDialog.open}
                onClose={() => setResultDialog({ open: false, type: "", message: "" })}
                type={resultDialog.type}
                message={resultDialog.message}
            />

            {/* Image Viewer Dialog */}
            <ImageViewerDialog
                open={imageViewDialog.open}
                onClose={() => setImageViewDialog({ open: false, imageUrl: "", fileName: "" })}
                imageUrl={imageViewDialog.imageUrl}
                fileName={imageViewDialog.fileName}
            />

            <Loader open={openLoader} />
        </>
    );
};

export default BriskTransactionHistory;
